import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/categories';
import { getServicesByCategory } from '@/lib/content';
import EmptyState from '@/components/base/EmptyState';

export default function Categories() {
  useSeo({
    title: '服务分类 - 在线服务平台',
    description: '浏览在线平台全部服务分类，涵盖教育、娱乐、购物、生活、健康、职场、科技、旅游等领域的在线服务信息。',
    keywords: '在线服务分类,服务分类,在线平台分类,服务类目',
  });

  const [openIds, setOpenIds] = useState<string[]>(['education']);

  const toggle = (id: string) => {
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">全部分类</span>
      </nav>

      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">服务分类</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          在线平台将服务信息划分为八大类目，每个类目下再细分多个子分类。你可以点开任意分类查看其子分类，或直接进入分类列表页，通过地区、服务类型和更新时间等条件精准筛选所需内容。
        </p>
      </header>

      {categories.length === 0 ? (
        <EmptyState icon="ri-folders-line" title="暂无服务分类" description="平台分类正在整理中，稍后再来看看吧。" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {categories.map((cat) => {
            const count = getServicesByCategory(cat.id).length;
            const isOpen = openIds.includes(cat.id);
            return (
              <div key={cat.id} className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggle(cat.id)}
                  className="w-full flex items-center gap-4 p-4 md:p-5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 text-2xl">
                    <i className={cat.icon}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-foreground-900">{cat.name}</h2>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{cat.description}</p>
                  </div>
                  <span className="shrink-0 text-xs text-secondary-600 hidden sm:block">{count} 项</span>
                  <i className={`shrink-0 text-xl text-foreground-400 transition-transform ${isOpen ? 'rotate-180' : ''} ri-arrow-down-s-line`}></i>
                </button>

                {isOpen && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5 pt-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {cat.subCategories.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/category/${cat.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-800 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-price-tag-3-line"></i>
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                    <Link
                      to={`/category/${cat.id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                    >
                      进入「{cat.name}」分类列表
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}